import { formatInclusions } from '@ast-data/shared';
import { type TranslateFn } from '../pdf/tokens';
import { splitNumbers } from '../../findings/descriptionValidation';
import { locationLabel } from '../../findings/locationLabel';
import type { ExportDescription } from './exportData';

/**
 * The sheet's wording: which of the two description kinds a row is about, and
 * how a stored value reads once it is printed. Pure string work — no layout, so
 * the blocks below stay about placement only.
 */

export function isPlanumDescription(description: ExportDescription): boolean {
    return description.kind === 'planum';
}

export function descriptionNumber(description: ExportDescription): string {
    return description.kind === 'planum'
        ? (description.planum?.planumNumber ?? '')
        : (description.profile?.profileNumber ?? '');
}

/** The Fläche/Sondage/parcel live on whichever description doc this is. */
export function spatialOf(description: ExportDescription) {
    return description.planum ?? description.profile ?? null;
}

/**
 * Planum and Profil carry the same field labels under two key prefixes; pick
 * the one matching this description so neither catalog drifts unnoticed.
 */
export function fieldLabel(
    translate: TranslateFn,
    description: ExportDescription,
    field: string
): string {
    const prefix = description.kind === 'planum' ? 'planum' : 'profile';
    return translate(`${prefix}.field.${field}`);
}

export function joinColors(doc: SoilDoc | null): string {
    return (doc?.colors ?? [])
        .map((entry) =>
            [entry.color, entry.munsellCode].filter(Boolean).join(' ')
        )
        .filter(Boolean)
        .join('; ');
}

/** Shared with the grid and the ExcaBook export — one spelling of a share. */
export function joinInclusions(doc: SoilDoc | null): string {
    return formatInclusions(doc?.inclusions ?? []);
}

/**
 * Whichever description carries the Bodenmerkmale. Normally the Planum; a
 * Befund that never showed in plan records them on its Profil instead, and
 * both docs hold the same eight fields.
 */
type SoilDoc = NonNullable<
    ExportDescription['planum'] | ExportDescription['profile']
>;

/** True when a Profil actually holds soil readings worth a box of their own. */
export function hasSoilData(doc: SoilDoc | null | undefined): boolean {
    if (!doc) {
        return false;
    }
    return Boolean(
        /**
         * `?? []` although the type says these are always arrays: a document
         * pushed by a backend that predates the field arrives WITHOUT it, and
         * outside dev mode RxDB has no validator to reject it. Reading .length
         * off that undefined took down the whole export with a stack trace
         * instead of printing one empty box (see relationText for the same).
         */
        (doc.colors ?? []).length ||
        (doc.inclusions ?? []).length ||
        doc.soilType ||
        doc.consistency ||
        doc.boundaryClarity ||
        doc.boundaryBasis ||
        doc.contour ||
        doc.humusContent
    );
}

/** "gut, aufgrund Farbe + Material" — the boundary clarity plus its basis. */
export function boundaryText(
    doc: SoilDoc | null,
    translate: TranslateFn
): string {
    if (!doc?.boundaryClarity && !doc?.boundaryBasis) {
        return '';
    }
    if (!doc.boundaryBasis) {
        return doc.boundaryClarity;
    }
    const basis = `${translate('planum.field.boundaryBasis')} ${doc.boundaryBasis}`;
    return doc.boundaryClarity ? `${doc.boundaryClarity}, ${basis}` : basis;
}

/** The "Teil von / identisch mit" refs, as Lagezuordnungen. */
export function relationText(
    description: ExportDescription,
    labelById: Map<string, string>,
    relation: 'identicalWith' | 'partOf' | 'inProfileWith'
): string {
    const doc = description.planum ?? description.profile;
    if (!doc) {
        return '';
    }
    // Same guard as hasSoilData: these ref arrays are young, and spreading an
    // undefined one throws before a single page is laid out.
    return [
        ...(doc[`${relation}PlanumIds`] ?? []),
        ...(doc[`${relation}ProfileIds`] ?? [])
    ]
        .map((id) => labelById.get(id) ?? '')
        .filter(Boolean)
        .join('; ');
}

/**
 * The slot a positional relation is read in — kind, Fläche and
 * Planum-/Profilnummer, normalized. Those fields store the BARE Befundnummer
 * (the ExcaBook wire format), and the form only ever offers Befunde described
 * on the SAME Fläche and Nummer: that is what lets a number be read back as a
 * Lagezuordnung. Built here and filled in `collectFindingExportData`, so both
 * sides spell the key the same way.
 */
export function strataSlotKey({
    kind,
    area,
    number,
    findingNumber
}: {
    kind: 'planum' | 'profile';
    area: string;
    /** The Planum- or Profilnummer, whichever kind this is. */
    number: string;
    findingNumber: string;
}): string {
    const normalize = (value: string) => value.trim().toLowerCase();
    return [
        kind,
        normalize(area),
        normalize(number),
        normalize(findingNumber)
    ].join('|');
}

/**
 * The positional relations („liegt über/schneidet", „zeitgleich mit",
 * „verfüllt" …) as Lagezuordnungen: "Bef. 7000, Fl. 1, Pl. 1" where the named
 * Befund is described in this slot, so the sheet reads like the form's chips
 * and like the identity relations two rows below. A number with no description
 * to go by — typed by hand, or imported from a sheet — still prints as
 * "Bef. 7000" rather than bare.
 */
export function strataRelationText(
    stored: string,
    description: ExportDescription,
    strataLabelBySlot: Map<string, string>
): string {
    const doc = spatialOf(description);
    if (!doc) {
        return '';
    }
    return splitNumbers(stored)
        .map(
            (findingNumber) =>
                strataLabelBySlot.get(
                    strataSlotKey({
                        kind: description.kind,
                        area: doc.area,
                        number: descriptionNumber(description),
                        findingNumber
                    })
                ) ?? locationLabel({ findingNumber })
        )
        .join('; ');
}

/**
 * The shared {@link Row} under this sheet's three measured label widths — the
 * body rows carry `wide`/`narrow` rather than a raw number, so the widths stay
 * declared in one place above.
 */
